import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Avatar,
  IconButton,
  useTheme,
  useMediaQuery,
  Menu,
  MenuItem,
  Box,
  Skeleton
} from '@mui/material';
import { FiMenu } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { useGetCurrentUser } from 'apis/user/hook';
import * as urls from 'routes/config';
import { useLogout } from 'apis/auth/hook';
import BusinessDropdown from './BusinessDropdown';
import { useGetAgencyOrganizations } from '@/apis/agency/organizations/hook';
import AgencyNotificationBell from '@/components/Notifications/AgencyNotificationBell';
import ProfileSettingsDialog from '@/pages/UserProfile/ProfileSettingsDialog';

interface AgencyHeaderProps {
  onMenuClick?: () => void;
}

export default function AgencyHeader({ onMenuClick }: AgencyHeaderProps) {
  const { mutate: doLogout } = useLogout();
  const theme = useTheme();
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const { data: currentUser, isLoading: isCurrentUserLoading } = useGetCurrentUser();
  const { data: organizations } = useGetAgencyOrganizations();

  const [settingsSection, setSettingsSection] = useState<'account' | 'notifications' | null>(null);

  return (
    <>
      <AppBar
        position='fixed'
        sx={{
          backgroundColor: theme.palette.background.paper,
          boxShadow: 'none',
          borderBottom: '1px solid #E0E0E0',
          top: 0,
          maxHeight: { xs: 56, lg: 64 },
          zIndex: 1200
        }}
      >
        <Toolbar
          sx={{
            justifyContent: isMediumScreen ? 'space-between' : 'space-between',
            minHeight: { xs: 56, lg: 64 },
            px: { xs: 1.5, lg: 3 }
          }}
        >
          {/* MOBILE VIEW HEADER */}
          {isMediumScreen ? (
            <>
              <Box display='flex' alignItems='center' gap={1}>
                <IconButton onClick={onMenuClick} sx={{ p: 0.75 }}>
                  <FiMenu size={22} />
                </IconButton>
              </Box>

              {/* Center Logo */}
              <Box
                component={Link}
                to={urls.agency.base}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  '& img': {
                    height: { xs: 32, lg: 40 },
                    paddingRight: { xs: 1, lg: 1.25 },
                    borderRight: isMediumScreen ? 'none' : '1px solid #cecece'
                  }
                }}
              >
                <img src={organizations?.logo_url} alt={organizations?.name} />
              </Box>

              {/* Notifications + avatar */}
              <Box display='flex' alignItems='center' gap={0.5}>
                {!isCurrentUserLoading && <AgencyNotificationBell />}
                {isCurrentUserLoading ? (
                  <Skeleton variant='circular' width={32} height={32} />
                ) : (
                  <IconButton onClick={handleClick} sx={{ p: 0 }}>
                    <Avatar
                      src={currentUser?.avatar_url}
                      alt={currentUser?.name || 'User'}
                      sx={{ width: 32, height: 32 }}
                    />
                  </IconButton>
                )}
              </Box>

              <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{ paper: { sx: { width: 260 } } }}
              >
                <MenuItem
                  onClick={() => {
                    handleClose();
                    setSettingsSection('account');
                  }}
                >
                  Profile
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    handleClose();
                    setSettingsSection('notifications');
                  }}
                >
                  Notification Preferences
                </MenuItem>
                <MenuItem onClick={() => doLogout()}>Logout</MenuItem>
              </Menu>
            </>
          ) : (
            /* DESKTOP VIEW */
            <>
              <Box display='flex' alignItems='center' color='#09090A' gap={{ xs: 0.5, lg: 2 }} flex={1} minWidth={0}>
                <Box
                  component={Link}
                  to={urls.agency.base}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    '& img': {
                      height: { xs: 32, lg: 40 },
                      paddingRight: { xs: 1, lg: 1.25 },
                      borderRight: '1px solid #cecece'
                    }
                  }}
                >
                  <img src={organizations?.logo_url} alt={organizations?.name} />
                </Box>
                <BusinessDropdown />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, lg: 1 }, flexShrink: 0 }}>
                {isCurrentUserLoading ? (
                  <>
                    <Skeleton
                      variant='text'
                      width={80}
                      height={20}
                      sx={{
                        marginRight: 1,
                        display: isMediumScreen ? 'none' : 'block'
                      }}
                    />
                    <Skeleton
                      variant='circular'
                      width={34}
                      height={34}
                      sx={{
                        display: isMediumScreen ? 'none' : 'block'
                      }}
                    />
                  </>
                ) : (
                  <>
                    <Box sx={{ display: isMediumScreen ? 'none' : 'flex', alignItems: 'center' }}>
                      <AgencyNotificationBell />
                    </Box>
                    <Typography
                      variant='body1'
                      sx={{
                        color: '#09090A',
                        marginRight: { xs: 0.5, lg: 1 },
                        display: isMediumScreen ? 'none' : 'inherit',
                        fontSize: { xs: '0.875rem', lg: '1rem' }
                      }}
                    >
                      {currentUser?.name}
                    </Typography>
                    <IconButton
                      onClick={handleClick}
                      sx={{
                        display: isMediumScreen ? 'none' : 'inherit',
                        padding: { xs: '4px', lg: '8px' }
                      }}
                    >
                      <Avatar
                        src={currentUser?.avatar_url}
                        alt={currentUser?.name || 'User'}
                        sx={{
                          width: { xs: 28, lg: 34 },
                          height: { xs: 28, lg: 34 }
                        }}
                      />
                    </IconButton>
                  </>
                )}
                {!isMediumScreen && !isCurrentUserLoading && (
                  <Menu
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'right'
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'right'
                    }}
                    slotProps={{ paper: { sx: { width: 260 } } }}
                  >
                    <MenuItem
                      onClick={() => {
                        handleClose();
                        setSettingsSection('account');
                      }}
                    >
                      Profile
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        handleClose();
                        setSettingsSection('notifications');
                      }}
                    >
                      Notification Preferences
                    </MenuItem>
                    <MenuItem onClick={() => doLogout()}>Logout</MenuItem>
                  </Menu>
                )}
              </Box>
            </>
          )}
        </Toolbar>
      </AppBar>
      <ProfileSettingsDialog
        open={settingsSection !== null}
        section={settingsSection ?? 'account'}
        scope='agency'
        onClose={() => setSettingsSection(null)}
      />
    </>
  );
}
